import type { ReactNode } from "react";
import { CheckCircle2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn, firstName } from "@/lib/utils";

export type CandidateDoneCardProps = {
  /** Candidate's full name; only the first name is shown in the greeting. */
  candidateName?: string | null;
  /** Test name, woven into the confirmation line when present. */
  testName?: string | null;
  /** Pre-formatted submission time (e.g. "14:32"); omitted when null. */
  submittedAtLabel?: string | null;
  /** Per-company completion message; replaces the default confirmation body when set. */
  completionMessage?: string | null;
  /**
   * Candidate-only score / per-question region. Passed by the real Done route;
   * omitted by the recruiter preview. When provided (even an empty fragment),
   * the content area always renders, matching the candidate screen exactly.
   */
  scoreSlot?: ReactNode;
  className?: string;
};

/**
 * The success-state card a candidate sees after submitting. Shared by the real
 * candidate Done route (`routes/Done.tsx`) and the recruiter's live preview in
 * Settings → Completion, so the preview can never drift from the real screen.
 */
export function CandidateDoneCard({
  candidateName,
  testName,
  submittedAtLabel,
  completionMessage,
  scoreSlot,
  className,
}: CandidateDoneCardProps) {
  const hasMessage = !!completionMessage && completionMessage.trim() !== "";
  return (
    <Card className={cn("w-full max-w-lg text-center", className)}>
      <CardHeader>
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
          <CheckCircle2 className="h-6 w-6 text-success" />
        </div>
        <CardTitle>
          Thanks{candidateName ? `, ${firstName(candidateName)}` : ""}.
        </CardTitle>
        <CardDescription
          className={cn("space-y-1 text-smd", hasMessage && "whitespace-pre-line")}
        >
          {hasMessage ? (
            completionMessage
          ) : (
            <>
              {testName ? (
                <>
                  Your submission for{" "}
                  <span className="font-medium text-foreground">{testName}</span>{" "}
                  was received
                </>
              ) : (
                "Your submission was received"
              )}
              {submittedAtLabel ? ` at ${submittedAtLabel}` : ""}. The recruiter
              will review and reach out by email if appropriate.
            </>
          )}
        </CardDescription>
      </CardHeader>
      {scoreSlot !== undefined && <CardContent>{scoreSlot}</CardContent>}
    </Card>
  );
}
